import { Icon } from "@iconify/react";
import Modal from "@mui/material/Modal";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { DataTable, DataTableBody, DataTableHeader, Input, MilestoneStepper } from "../../components";
import { formatAmount, formatDate } from "../../lib/utils";
import { useJob } from "../../services";

function AccountsJobView() {
  const { getJobDetailsById } = useJob();
  const { id } = useParams();
  const [jobData, setJobData] = useState<Record<string, any>>({});
  const [milestonesData, setMilestonesData] = useState([]);
  const [isSectionVisible, setIsSectionVisible] = useState(true);
  const [hideJobDetails, setHideJobDetails] = useState(true);
  const handleSectionToggle = () => {
    const currentValue = isSectionVisible;
    setIsSectionVisible(!currentValue);
    setHideJobDetails(currentValue);
  };
  const [loadStatus, setLoadStatus] = useState({ loading: false, error: null });
  const [showAddCredit, setShowAddCredit] = useState(false);

  const paymentDetails = useMemo(() => {
    const totalBeforeGST = 120000;
    const percent = 10;

    return milestonesData.map((milestone, index) => {
      const amountCalculated = totalBeforeGST * (percent / 100);
      const amount = index === 4 ? 2000 : index === 5 ? amountCalculated + 10000 : amountCalculated;
      const gst = amount * 0.18;
      const totalAfterGST = amount + gst;
      const paymentMadeFor = index < 5 ? "amount" : "unpaid";

      return {
        milestone_number: milestone.milestone_number,
        payment_percent: `${percent}%`,
        amount_with_gst: formatAmount(totalAfterGST),
        gst_amount: formatAmount(gst),
        amount: formatAmount(amount),
        payment_made_for: paymentMadeFor,
      };
    });
  }, [milestonesData]);

  const grossValue = 120000 + 120000 * 0.18;
  const amountCredited = 50000;
  const amountRequested = 75000;
  const balance = grossValue - amountCredited - amountRequested;

  const totalPercent = useMemo(() => {
    let total = 0;
    for (let i = 0; i < milestonesData.length; i++) {
      total += milestonesData[i].payment_percent;
    }
    return total;
  }, [milestonesData]);
  const totalAmount = 120000;
  const totalGst = totalAmount * 0.18;
  const totalAmountWithGst = totalAmount + totalGst;

  const [paymentsMade, setPaymentsMade] = useState([]);

  useEffect(() => {
    setPaymentsMade([
      {
        tds_percent: "10%",
        tds_amount: formatAmount(5000),
        amount_credited: formatAmount(45000),
        invoice_amount: formatAmount(50000),
        status: "invoice_sent",
      },
      {
        tds_percent: "10",
        tds_amount: formatAmount(7500),
        amount_credited: formatAmount(67500),
        invoice_amount: formatAmount(75000),
        status: "proforma_sent",
      },
    ]);
  }, []);

  const fetchJobDetails = useCallback(async () => {
    setLoadStatus({ loading: true, error: null });
    const { job, error } = await getJobDetailsById(id);
    console.log(job);
    if (error) setLoadStatus({ loading: false, error: error });
    else {
      const { milestones, ...jobData } = job;
      setJobData(jobData);
      setMilestonesData(milestones);
      setLoadStatus({ loading: false, error: null });
    }
  }, [getJobDetailsById, id]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const paymentOverviewTableColumns = [
    { id: "milestone_number", label: "Milestone" },
    // payment_percent, amount, GST amount, Amount with GST, payment requested
    { id: "payment_percent", label: "Payment Percent" },
    { id: "amount", label: "Amount (₹)" },
    { id: "gst_amount", label: "GST Amount (₹)" },
    { id: "amount_with_gst", label: "Amount with GST (₹)" },
    { id: "payment_requested", label: "Payment Requested" },
  ];

  const paymentsMadeTableColumns = [
    // TDS %, TDS amount, Amount credited, Invoice amount, proforma, invoice
    { id: "tds_percent", label: "TDS %" },
    { id: "tds_amount", label: "TDS Amount (₹)" },
    { id: "amount_credited", label: "Amount Credited (₹)" },
    { id: "invoice_amount", label: "Invoice Amount (₹)" },
    { id: "proforma", label: "Proforma" },
    { id: "invoice", label: "Invoice" },
  ];

  const renderPaymentOverviewTable = () => (
    <DataTable columnCount={paymentOverviewTableColumns.length}>
      <DataTableHeader columns={paymentOverviewTableColumns} noSort />
      <DataTableBody loading={loadStatus.loading} isFull>
        {paymentDetails?.map((item, index) => (
          <div key={index} className="data_table_row">
            {paymentOverviewTableColumns.map((header) =>
              header.id === "payment_requested" ? (
                <div className="data_table_cell action" key={header.id}>
                  {index + 1 < jobData.milestones_completed ? (
                    <Link to={`https://example.com/payment-request-${index + 1}`} target="_blank" className="ghost_btn">
                      view
                    </Link>
                  ) : (
                    "-"
                  )}
                </div>
              ) : (
                <div
                  key={header.id}
                  className={`data_table_cell ${
                    item.payment_made_for === "amount"
                      ? header.id === "amount"
                        ? "text-green"
                        : header.id === "gst_amount"
                        ? "font-md text-red"
                        : ""
                      : ""
                  } ${
                    item.payment_made_for === "unpaid" && header.id === "amount_with_gst"
                      ? ["amount", "full"].includes(paymentDetails[index - 2].payment_made_for)
                        ? "font-md text-red"
                        : "text-primary"
                      : ""
                  }`}
                >
                  {item[header.id]}
                </div>
              )
            )}
          </div>
        ))}
        <div className="data_table_row font-semibold">
          <div className="data_table_cell"></div>
          <div className="data_table_cell">{totalPercent}%</div>
          <div className="data_table_cell">{formatAmount(totalAmount)}</div>
          <div className="data_table_cell">{formatAmount(totalGst)}</div>
          <div className="data_table_cell">{formatAmount(totalAmountWithGst)}</div>
        </div>
      </DataTableBody>
    </DataTable>
  );

  const renderPaymentsMadeTable = () => (
    <DataTable columnCount={paymentsMadeTableColumns.length}>
      <DataTableHeader columns={paymentsMadeTableColumns} noSort />
      <DataTableBody loading={loadStatus.loading} isFull>
        {paymentsMade?.map((item, index) => (
          <div key={index} className="data_table_row">
            {paymentsMadeTableColumns.map((header) =>
              header.id === "proforma" ? (
                <div className="data_table_cell action" key={header.id}>
                  <Link to={`https://example.com/${header.id}-${index + 1}`} target="_blank" className="ghost_btn">
                    view
                  </Link>
                </div>
              ) : header.id === "invoice" ? (
                <div className="data_table_cell action" key={header.id}>
                  {item.status === "invoice_sent" ? (
                    <Link to={`https://example.com/${header.id}-${index + 1}`} target="_blank" className="ghost_btn">
                      view
                    </Link>
                  ) : (
                    "-"
                  )}
                </div>
              ) : (
                <div key={header.id} className="data_table_cell">
                  {item[header.id]}
                </div>
              )
            )}
          </div>
        ))}
      </DataTableBody>
    </DataTable>
  );

  return (
    <>
      <div className="card rounded column">
        <div className="row">
          <span>Job Code {jobData.job_code}</span>
          <span>{jobData.job_name}</span>
          <span>
            {jobData.milestones_completed ?? 0}/{jobData.milestone_count} Milestones
          </span>
          <div className="column shrink self-start fit">
            <span className="font-md">{jobData.milestone_status}</span>
            <span className="small text_center">{formatDate(jobData.milestone_achieve_date)}</span>
          </div>
        </div>
        <div className="row">
          <div className="row shrink">
            <span className="font-md">Gross Amount</span>
            <span>{formatAmount(grossValue)}</span>
          </div>
          <div className="row shrink">
            <span className="font-md">Amount Credited</span>
            <span className="text-green">{formatAmount(amountCredited)}</span>
          </div>
          <div className="row shrink">
            <span className="font-md">Amount Requested</span>
            <span className="text-red">{formatAmount(amountRequested)}</span>
          </div>
          <div className="row shrink">
            <span className="font-md">Balance</span>
            <span className="text-primary">{formatAmount(balance)}</span>
          </div>
        </div>
      </div>
      <div className="card column spread">
        <div className="row">
          <h3 className="section_title">Milestone Details</h3>
          <button onClick={handleSectionToggle} className="btn toggle_btn">
            <Icon icon="mingcute:down-line" className={`text-lg toggle_icon ${isSectionVisible ? "" : "hide"}`} />
          </button>
        </div>

        <MilestoneStepper milestonesData={milestonesData} jobData={jobData} />
        {isSectionVisible && (
          <section className="column">
            <section className="column shrink">
              <div className="row">
                <h3 className="section_title">{jobData.job_name}</h3>
                <button type="button" className="btn cta_outline_btn" onClick={() => {}}>
                  Payment Request
                </button>
              </div>
              {renderPaymentOverviewTable()}
            </section>
            <section className="column shrink">
              <div className="row">
                <h3 className="section_title">{jobData.job_name}</h3>
                <div className="row end">
                  <button type="button" className="btn cta_outline_btn" onClick={() => {}}>
                    Generate Proforma{" "}
                  </button>
                  <button type="button" className="btn cta_outline_btn" onClick={() => {}}>
                    Generate Invoice
                  </button>
                  <button type="button" className="btn cta_btn" onClick={() => setShowAddCredit(true)}>
                    Add Credit
                  </button>
                </div>
              </div>
              {renderPaymentsMadeTable()}
            </section>
          </section>
          // <div className="card rounded column">
          //   {milestonesData.map((milestone, index) => (
          //     <div className="card rounded column">
          //       <div className="font-md">
          //         Milestone {index + 1}/{milestonesData.length}
          //       </div>
          //       <div className="grid-2-cols-with-divider">
          //         <div className="column">
          //           <span className="label">1. Proforma</span>
          //           {milestone.proforma ? (
          //             <div className="row small">
          //               <span className="label">{milestone.proforma.name}</span>
          //               <Link to={milestone.proforma.url} className="btn ghost_btn">
          //                 Preview
          //               </Link>
          //             </div>
          //           ) : (
          //             <div className="row">
          //               <input type="text" placeholder="Enter proforma name" className="input half" />
          //               <button className="btn cta_outline_btn" disabled={index + 1 > jobData.milestones_completed + 1}>
          //                 Generate
          //               </button>
          //             </div>
          //           )}
          //         </div>
          //         <div className="column">
          //           <span className="label">2. Invoice</span>
          //           {milestone.invoice ? (
          //             <div className="row small">
          //               <span className="label">{milestone.invoice.name}</span>
          //               <Link to={milestone.invoice.url} className="btn ghost_btn">
          //                 Preview
          //               </Link>
          //             </div>
          //           ) : (
          //             <div className="row">
          //               <input type="text" placeholder="Enter invoice name" className="input half" />
          //               <button className="btn cta_outline_btn" disabled={index + 1 > jobData.milestones_completed + 1}>
          //                 Generate
          //               </button>
          //             </div>
          //           )}
          //         </div>
          //       </div>
          //     </div>
          //   ))}
          // </div>
        )}
      </div>
      {/* <JobDetails jobData={jobData} showDetails={hideJobDetails} hideAssignWork hideEdit hideTypeOfWork /> */}
      <AddCreditModal
        open={showAddCredit}
        onClose={() => setShowAddCredit(false)}
        onSubmit={() => setShowAddCredit(false)}
      />
    </>
  );
}

const ModalWrapper = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const AddCreditModal = ({ open, onClose, onSubmit }) => {
  const initialFormData = useMemo(
    () => ({
      amount_credited: "",
      payment_date: "",
      project_name: "",
      job_code: "",
      amount_requested: "",
      payment_request_id: "",
      purchase_order_no: "",
      is_tds_included: "",
      tds_percent: "",
      is_gst_included: "",
      gst_number: "",
    }),
    []
  );
  const [formData, setFormData] = useState(initialFormData);

  const handleSubmit = (e) => {
    e.preventDefault();
    // onSubmit(formData);
    setFormData(initialFormData);
    onClose();
  };

  return (
    <ModalWrapper open={open} onClose={onClose} aria-labelledby="add-credit-modal" aria-describedby="add-credit-modal">
      <form id="add-credit-form" onSubmit={handleSubmit} className="modal_container column full small">
        <h3 className="form-section-title">Add Credit</h3>
        <div className="grid-2-cols">
          <div className="label_grid align_center full">
            <span className="label gray">Amount credited</span>
            <Input
              type="number"
              value={formData.amount_credited}
              onChange={(e) => setFormData({ ...formData, amount_credited: e.target.value })}
              prefix="₹"
              min={0}
            />
          </div>
          <div className="label_grid align_center full">
            <span className="label gray">Date of payment</span>
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="input"
            />
          </div>
        </div>
        <div className="grid-2-cols">
          <div className="label_grid align_center full">
            <span className="label gray">Project name</span>
            <input
              type="search"
              value={formData.project_name}
              onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
              className="input"
            />
          </div>
          <div className="label_grid align_center full">
            <span className="label gray">Job code</span>
            <input
              type="search"
              value={formData.job_code}
              onChange={(e) => setFormData({ ...formData, job_code: e.target.value })}
              className="input"
            />
          </div>
        </div>
        <div className="grid-2-cols">
          <div className="label_grid align_center full">
            <span className="label gray">Payment request amount</span>
            <Input
              type="number"
              value={formData.amount_requested}
              onChange={(e) => setFormData({ ...formData, amount_requested: e.target.value })}
              prefix="₹"
              min={0}
            />
          </div>
          <div className="label_grid align_center full">
            <span className="label gray">Against payment request</span>
            <input
              type="search"
              value={formData.payment_request_id}
              onChange={(e) => setFormData({ ...formData, payment_request_id: e.target.value })}
              className="input"
            />
          </div>
        </div>
        <div className="label_grid align_center start">
          <span className="label gray">Purchase order number</span>
          <input
            type="text"
            value={formData.purchase_order_no}
            onChange={(e) => setFormData({ ...formData, purchase_order_no: e.target.value })}
            className="input"
          />
        </div>
        <div className="grid-2-cols">
          <div className="label_grid align_center start">
            <span className="label gray">TDS included</span>
            <div className="row ">
              <label htmlFor="tds_yes" className="label">
                <input
                  type="radio"
                  name="is_tds_included"
                  id="tds_yes"
                  value="true"
                  checked={formData.is_tds_included === "true"}
                  onChange={(e) => setFormData({ ...formData, is_tds_included: e.target.value })}
                />
                <span className="label">Yes</span>
              </label>
              <label htmlFor="tds_no" className="label">
                <input
                  type="radio"
                  name="is_tds_included"
                  id="tds_no"
                  value="false"
                  checked={formData.is_tds_included === "false"}
                  onChange={(e) => setFormData({ ...formData, is_tds_included: e.target.value })}
                />
                <span className="label">No</span>
              </label>
            </div>
          </div>
          {formData.is_tds_included === "true" && (
            <div className="label_grid align_center full">
              <span className="label gray">TDS percent</span>
              <input
                type="number"
                value={formData.tds_percent}
                onChange={(e) => setFormData({ ...formData, tds_percent: e.target.value })}
                className="input"
              />
            </div>
          )}
        </div>
        <div className="grid-2-cols">
          <div className="label_grid align_center start">
            <span className="label gray">GST included</span>
            <div className="row">
              <label htmlFor="gst_yes" className="label">
                <input
                  type="radio"
                  id="gst_yes"
                  name="is_gst_included"
                  value="true"
                  checked={formData.is_gst_included === "true"}
                  onChange={(e) => setFormData({ ...formData, is_gst_included: e.target.value })}
                />
                <span className="label">Yes</span>
              </label>
              <label htmlFor="gst_no" className="label">
                <input
                  type="radio"
                  name="is_gst_included"
                  value="false"
                  checked={formData.is_gst_included === "false"}
                  onChange={(e) => setFormData({ ...formData, is_gst_included: e.target.value })}
                />
                <span className="label">No</span>
              </label>
            </div>
          </div>
          {formData.is_gst_included === "true" && (
            <div className="label_grid align_center full">
              <span className="label gray">GST number</span>
              <input
                type="text"
                value={formData.gst_number}
                onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                className="input"
              />
            </div>
          )}
        </div>
        <div className="row end">
          <button type="button" className="btn ghost_outline" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn cta_outline_btn">
            Submit
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default AccountsJobView;
